import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { COLORS, MAX_COLORS, CONDITIONS, BRANDS } from "@/lib/itemAttributes";

const chip = (on) => `min-h-[44px] inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium select-none transition-[background-color,border-color,transform] duration-150 active:scale-95 ${on ? "border-[#191A54] bg-[#191A54] text-white" : "border-border bg-white text-slate-700 hover:border-[#191A54]/50"}`;

// Native "required" check for chip groups (invisible input the browser can point at).
const RequiredProxy = ({ value, testid }) => (
  <input tabIndex={-1} aria-hidden="true" required value={value || ""} onChange={() => {}}
    className="absolute left-1/2 bottom-0 h-px w-px opacity-0 pointer-events-none" data-testid={testid} />
);

const splitList = (v) => (v || "").split(",").map((s) => s.trim()).filter(Boolean);

export const ColorChips = ({ value, onChange, prefix, required }) => {
  const { t } = useLang();
  const selected = splitList(value);
  const toggle = (label) => {
    const on = selected.includes(label);
    if (!on && selected.length >= MAX_COLORS) return;
    onChange((on ? selected.filter((s) => s !== label) : [...selected, label]).join(", "));
  };
  return (
    <div className="relative" data-testid={`${prefix}-color`}>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {COLORS.map((c) => {
          const label = t(`col.${c.key}`);
          const on = selected.includes(label);
          return (
            <button key={c.key} type="button" aria-pressed={on} onClick={() => toggle(label)} className={chip(on)} data-testid={`${prefix}-color-${c.key}`}>
              <span className="h-5 w-5 rounded-full border border-black/15 shrink-0 grid place-items-center" style={{ background: c.hex }}>
                {on && <Check size={12} className={["white", "beige", "yellow", "silver", "gold"].includes(c.key) ? "text-slate-900" : "text-white"} />}
              </span>
              {label}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-slate-400">{t("col.max")}</p>
      {required && <RequiredProxy value={value} testid={`${prefix}-color-required`} />}
    </div>
  );
};

export const ConditionChips = ({ value, onChange, prefix, required }) => {
  const { t } = useLang();
  return (
    <div className="relative mt-1.5 flex flex-wrap gap-2" data-testid={`${prefix}-condition`}>
      {CONDITIONS.map((k) => {
        const label = t(`cond.${k}`);
        const on = value === label;
        return (
          <button key={k} type="button" aria-pressed={on} onClick={() => onChange(on ? "" : label)} className={chip(on)} data-testid={`${prefix}-condition-${k}`}>
            {on && <Check size={14} />}{label}
          </button>
        );
      })}
      {required && <RequiredProxy value={value} testid={`${prefix}-condition-required`} />}
    </div>
  );
};

export const BrandChips = ({ value, onChange, category, prefix }) => {
  const { t } = useLang();
  const brands = BRANDS[category] || [];
  const unknown = t("brand.unknown");
  const [otherOpen, setOtherOpen] = useState(false);
  const isCustom = !!value && value !== unknown && !brands.includes(value);
  // Restored drafts arrive after first render: reopen "Other" for a custom brand.
  useEffect(() => { if (isCustom) setOtherOpen(true); }, [isCustom]);
  // A new category invalidates a brand picked from the previous category's list.
  const prevCat = useRef(category);
  useEffect(() => {
    if (prevCat.current !== category && prevCat.current !== undefined && (BRANDS[prevCat.current] || []).includes(value) && !brands.includes(value)) onChange("");
    prevCat.current = category;
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (v) => { setOtherOpen(false); onChange(value === v ? "" : v); };
  return (
    <div data-testid={`${prefix}-brand`}>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {brands.map((b) => (
          <button key={b} type="button" aria-pressed={!otherOpen && value === b} onClick={() => pick(b)} className={chip(!otherOpen && value === b)} data-testid={`${prefix}-brand-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            {!otherOpen && value === b && <Check size={14} />}{b}
          </button>
        ))}
        <button type="button" aria-pressed={otherOpen} onClick={() => { setOtherOpen(!otherOpen); onChange(""); }} className={chip(otherOpen)} data-testid={`${prefix}-brand-other`}>
          {otherOpen && <Check size={14} />}{t("brand.other")}
        </button>
        <button type="button" aria-pressed={!otherOpen && value === unknown} onClick={() => pick(unknown)} className={chip(!otherOpen && value === unknown)} data-testid={`${prefix}-brand-unknown`}>
          {!otherOpen && value === unknown && <Check size={14} />}{unknown}
        </button>
      </div>
      {otherOpen && (
        <Input autoFocus value={value} onChange={(e) => onChange(e.target.value)} placeholder={t("brand.otherPh")}
          className="mt-2 h-12 rounded-xl border-findo-navy/20" data-testid={`${prefix}-brand-input`} />
      )}
    </div>
  );
};
